import { useMyProfile } from '@hobbytools/data';
import { Icon } from '@hobbytools/icons';
import { Button } from '@hobbytools/ui';
import type { ReactNode } from 'react';
import { type AppId, apps, appUrl } from './apps';
import { useAuth } from './auth/AuthProvider';
import { currentPath, signInUrl } from './auth/urls';

export interface AppShellProps {
  /** The app rendering the shell; highlights it in the system switcher. */
  current: AppId;
  children: ReactNode;
  /** App-specific navigation shown under the header (router links). */
  nav?: ReactNode;
}

function UserArea() {
  const { user, isLoading, signOut } = useAuth();
  if (isLoading) return null;
  if (!user) {
    return (
      <a
        href={signInUrl(currentPath(window.location))}
        className="rounded-md px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-soft"
      >
        Sign in
      </a>
    );
  }
  return (
    <SignedInArea
      userId={user.id}
      fallbackName={(user.user_metadata?.display_name as string | undefined) ?? user.email ?? 'Player'}
      onSignOut={signOut}
    />
  );
}

function SignedInArea({
  userId,
  fallbackName,
  onSignOut,
}: {
  userId: string;
  fallbackName: string;
  onSignOut: () => Promise<void>;
}) {
  const profile = useMyProfile(userId);
  const name = profile.data?.display_name ?? fallbackName;
  return (
    <div className="flex items-center gap-2">
      <a
        href={appUrl('hub', '/profile')}
        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-fg-muted hover:bg-sunken"
        data-testid="current-user"
      >
        <Icon name="user" />
        {name}
      </a>
      <Button
        variant="ghost"
        size="sm"
        onPress={async () => {
          await onSignOut();
          window.location.assign(appUrl('hub'));
        }}
      >
        Sign out
      </Button>
    </div>
  );
}

/** Shared chrome for every app so moving between /, /40k/, … feels like one product. */
export function AppShell({ current, children, nav }: AppShellProps) {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-fg">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <a href={appUrl('hub')} className="heading-display flex items-center gap-2 text-lg">
            <Icon name="dice" size={22} />
            Hobby Tools
          </a>
          <nav aria-label="Game systems" className="flex gap-1">
            {apps
              .filter((a) => a.enabled && a.id !== 'hub')
              .map((a) => (
                <a
                  key={a.id}
                  href={appUrl(a.id as AppId)}
                  aria-current={a.id === current ? 'page' : undefined}
                  className="rounded-md px-3 py-1.5 text-sm text-fg-muted hover:bg-sunken aria-[current=page]:bg-accent-soft aria-[current=page]:text-accent-fg"
                >
                  {a.shortName ?? a.name}
                </a>
              ))}
          </nav>
          <div className="ml-auto">
            <UserArea />
          </div>
        </div>
        {nav ? <div className="mx-auto max-w-6xl px-4 pb-2">{nav}</div> : null}
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">{children}</main>
    </div>
  );
}
